import React, { useEffect, useState } from "react";
import axios from "axios";
import { Form, Button, Card, Spinner } from "react-bootstrap";

const apiBase = process.env.REACT_APP_SERVER_URL;

export default function SlotSettingsPage() {
    const [settings, setSettings] = useState({
        start_time: "",
        end_time: "",
        interval_minutes: ""
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // ✅ Fetch current slot settings
    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${apiBase}/api/slot-set`);
            if (res.data) {
                setSettings({
                    start_time: res.data.start_time?.slice(0, 5) || "",
                    end_time: res.data.end_time?.slice(0, 5) || "",
                    interval_minutes: res.data.interval_minutes || ""
                });
            }
        } catch (err) {
            console.error("Error fetching slot settings:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    // ✅ Update slot settings
    const handleUpdate = async (e) => {
        e.preventDefault();
        setSaving(true);

        // Validate interval
        const interval = parseInt(settings.interval_minutes, 10);
        if (interval <= 0 || interval > 1440) {
            alert("Please enter a valid interval between 1 and 1440 minutes.");
            setSaving(false);
            return;
        }

        // Ensure start_time and end_time are valid 24-hour format HH:mm
        const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;

        if (!timeRegex.test(settings.start_time) || !timeRegex.test(settings.end_time)) {
            alert("Please enter valid start and end times in HH:mm (24-hour format).");
            setSaving(false);
            return;
        }

        try {
            await axios.put(`${apiBase}/api/slot-set`, settings);
            await fetchSettings(); // refresh after update
        } catch (err) {
            console.error("Error updating slot settings:", err);
        } finally {
            setSaving(false);
        }
    };

    // Handle time change and ensure correct format
    const handleTimeChange = (field, value) => {
        // Validate 24-hour time format
        const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
        if (!timeRegex.test(value)) {
            alert("Invalid time format. Please use HH:mm (24-hour format).");
            return;
        }
        setSettings({ ...settings, [field]: value });
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <Spinner animation="border" />
            </div>
        );
    }

    return (
        <div className="container py-4">
            <Card className="shadow-sm p-4">
                <h4 className="mb-3">⚙️ Slot Settings</h4>

                <Form onSubmit={handleUpdate}>
                    <Form.Group className="mb-3">
                        <Form.Label>Start Time</Form.Label>
                        <Form.Control
                            type="time"
                            value={settings.start_time}
                            onChange={(e) => handleTimeChange("start_time", e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>End Time</Form.Label>
                        <Form.Control
                            type="time"
                            value={settings.end_time}
                            onChange={(e) => handleTimeChange("end_time", e.target.value)}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Interval (minutes)</Form.Label>
                        <Form.Control
                            type="number"
                            min="1"
                            value={settings.interval_minutes}
                            onChange={(e) => setSettings({ ...settings, interval_minutes: e.target.value })}
                        />
                    </Form.Group>

                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving ? "Updating..." : "Update Settings"}
                    </Button>
                </Form>
            </Card>
        </div>
    );
}
